import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { listConfig, setConfig, rebalanceServiceGroups } from '../../services/cloneProvider';
import { getErrorMessage } from './badges';

const CONFIG_GROUPS = [
  {
    title: 'Clone Manager Tunables',
    fields: ['HOUR_TEMP_BAN', 'HOUR_TO_DELAY_RECOVER', 'ACCOUNT_REQUEST_DELAY_SEC', 'NUM_ACCOUNTS_TO_CALC_SCORE'],
    risk: false,
  },
  {
    title: 'Job Intervals (giây)',
    fields: [
      'RECOVERY_INTERVAL_SECONDS',
      'HEALTH_CHECK_INTERVAL_SECONDS',
      'BUY_INTERVAL_SECONDS',
      'PERSIST_INTERVAL_SECONDS',
      'PRODUCT_EVALUATION_INTERVAL_SECONDS',
    ],
    risk: false,
  },
  {
    title: 'Job On/Off (xem thêm tab Jobs cho FBQL/EAAAAU/EAAGNO)',
    fields: ['ENABLED_PRODUCT_EVALUATION'],
    risk: false,
  },
  {
    title: 'Buy Thresholds & Amounts (theo service_type)',
    fields: [
      'BUY_THRESHOLD_FBQL', 'BUY_THRESHOLD_EAAAAU', 'BUY_THRESHOLD_EAAGNO',
      'BUY_AMOUNT_FBQL', 'BUY_AMOUNT_EAAAAU', 'BUY_AMOUNT_EAAGNO',
    ],
    risk: true,
  },
  {
    title: 'Buy Algorithm',
    fields: [
      'BUY_ACCEPTABLE_SCORE', 'BUY_TRY_NEW_TYPE',
      'BUY_TRY_NEW_TYPE_MIN_PRICE_VND', 'BUY_TRY_NEW_TYPE_MAX_PRICE_VND', 'BUY_TRY_NEW_TYPE_MAX_SPEND_VND',
    ],
    risk: true,
  },
  {
    title: 'Service Group Ratio',
    fields: ['SERVICE_GROUP_RATIO_FBQL', 'SERVICE_GROUP_RATIO_EAAAAU', 'SERVICE_GROUP_RATIO_EAAGNO'],
    risk: true,
    rebalanceAll: true,
  },
];

function inputTypeFor(value) {
  const lower = String(value).toLowerCase();
  if (lower === 'true' || lower === 'false') return 'bool';
  if (value !== '' && !Number.isNaN(Number(value))) return 'number';
  return 'text';
}

function ConfigField({ fieldKey, value, onSaved }) {
  const type = inputTypeFor(value);
  // Chuẩn hoá về đúng "true"/"false" lowercase cho bool - backend đôi khi
  // trả "True"/"False" (Python's str(bool)), nhưng <select> dưới chỉ có
  // option lowercase nên cần khớp đúng mới hiện được lựa chọn hiện tại.
  const normalizedValue = type === 'bool' ? String(value).toLowerCase() : value;
  const [draft, setDraft] = useState(normalizedValue);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setDraft(normalizedValue), [normalizedValue]);

  async function handleSave() {
    setIsSaving(true);
    try {
      await setConfig(fieldKey, String(draft));
      toast.success(`Đã lưu ${fieldKey}.`);
      onSaved(fieldKey, String(draft));
    } catch (error) {
      toast.error(getErrorMessage(error));
      setDraft(normalizedValue);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0">
      <span className="text-sm text-slate-700">{fieldKey}</span>
      <div className="flex items-center gap-2">
        {type === 'bool' ? (
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="rounded-lg border border-slate-200 px-2 py-1 text-sm"
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type={type === 'number' ? 'number' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-40 rounded-lg border border-slate-200 px-2 py-1 text-sm"
          />
        )}
        <button
          type="button"
          disabled={isSaving || draft === normalizedValue}
          onClick={handleSave}
          className="rounded-lg bg-cyan-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-cyan-700 disabled:opacity-40"
        >
          Lưu
        </button>
      </div>
    </div>
  );
}

export default function ConfigTab() {
  const [config, setConfigState] = useState(null);
  const [isRebalancing, setIsRebalancing] = useState(false);

  async function handleRebalanceAll() {
    if (!window.confirm('Rebalance service group cho TẤT CẢ purchase record theo tỉ lệ cấu hình hiện tại?')) return;
    setIsRebalancing(true);
    try {
      const data = await rebalanceServiceGroups();
      toast.success(`Đã rebalance ${data.count} account.`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsRebalancing(false);
    }
  }

  const fetchData = useCallback(async () => {
    try {
      const data = await listConfig();
      setConfigState(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!config) {
    return <div className="rounded-2xl border border-cyan-100 bg-white p-6 text-center text-sm text-slate-500">Đang tải...</div>;
  }

  const knownFields = new Set(CONFIG_GROUPS.flatMap((g) => g.fields));
  const otherFields = Object.keys(config).filter((k) => !knownFields.has(k));

  function handleSaved(key, value) {
    setConfigState((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="space-y-3">
      {CONFIG_GROUPS.map((group) => (
        <div
          key={group.title}
          className={[
            'rounded-2xl border bg-white p-4',
            group.risk ? 'border-orange-300' : 'border-cyan-100',
          ].join(' ')}
        >
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">{group.title}</h3>
            {group.rebalanceAll && (
              <button
                type="button"
                disabled={isRebalancing}
                onClick={handleRebalanceAll}
                className="rounded-lg bg-cyan-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-cyan-700 disabled:opacity-40"
              >
                Rebalance tất cả
              </button>
            )}
          </div>
          <div>
            {group.fields
              .filter((f) => f in config)
              .map((f) => <ConfigField key={f} fieldKey={f} value={config[f]} onSaved={handleSaved} />)}
          </div>
        </div>
      ))}

      {otherFields.length > 0 && (
        <div className="rounded-2xl border border-cyan-100 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-900">Khác</h3>
          <div>
            {otherFields.map((f) => <ConfigField key={f} fieldKey={f} value={config[f]} onSaved={handleSaved} />)}
          </div>
        </div>
      )}
    </div>
  );
}
